import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { BranchType } from '../../../../interfaces/delivery-company/branches';
import MoneyInput from '../../../common/money-input/money-input';
import { Money } from '../../../../utils/money';
import { useSnackBar } from '../../../../contexts/snackbar';
import { useSelectedBranch } from '../../../../contexts/selected-branch-context';
import { displayMoney } from '../../../../utils/money';
import { PaginatedResponse, usePaginatedData } from '../../../../hooks/pagination';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import FilterChips from '../../../common/filter-chips/filter-chips';
import GenericTable from '../../../common/generic-table/generic-table';
import { showBranchInvoiceState, canCancelBranchInvoice } from '../../../../utils/branch-invoices';
import { BranchInvoiceState, IBranchInvoice } from '../../../../interfaces/common/branch-invoices';
import BranchInvoiceDetail from './branch-invoice-detail';
import CancelIcon from '@mui/icons-material/Cancel';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';

// Using IBranchInvoice from interfaces

function BranchTransfers() {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const { selectedBranch } = useSelectedBranch();

  const [branches, setBranches] = useState<BranchType[]>([]);
  const [toBranch, setToBranch] = useState<BranchType | null>(null);
  const [amount, setAmount] = useState<Money[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [directionFilter, setDirectionFilter] = useState<'incoming' | 'outgoing'>('outgoing');
  const [selectedInvoice, setSelectedInvoice] = useState<IBranchInvoice | null>(null);
  const [cancellingInvoice, setCancellingInvoice] = useState<IBranchInvoice | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState<IBranchInvoice | null>(null);
  const loadBranches = async () => {
    try {
      setLoadingBranches(true);
      const res = await axios.get(
        `${API_ENDPOINT}/api/v1/branches/`,
        config
      );
      const results: BranchType[] = res.data?.results ?? res.data ?? [];
      setBranches(results);
    } catch {
      showMessage('Failed to load branches', 'error');
    } finally {
      setLoadingBranches(false);
    }
  };

  useEffect(() => {
    loadBranches();
  }, []);

  const {
    data: invoices,
    loading: loadingInvoices,
    refetch: refetchInvoices,
  } = usePaginatedData<IBranchInvoice>(
    async (page: number, pageSize: number): Promise<PaginatedResponse<IBranchInvoice>> => {
      const res = await axios.get(
        `${API_ENDPOINT}/api/v1/branch-invoices/`,
        {
          ...config,
          params: {
            page: page + 1, // hook uses 0-based page
            page_size: pageSize,
            fields: 'id,from_branch,to_branch,status,created_at,amount',
            ...(directionFilter === 'incoming' && selectedBranch?.id
              ? { to_branch: selectedBranch.id }
              : {}),
            ...(directionFilter === 'outgoing' && selectedBranch?.id
              ? { from_branch: selectedBranch.id }
              : {}),
          },
        }
      );
      const data = res.data;
      if (Array.isArray(data)) {
        return {
          count: data.length,
          results: data,
          next: null,
          previous: null,
        };
      }
      return data;
    },
    [directionFilter, selectedBranch?.id],
    false
  );

  const handleCreateTransfer = async (onSuccess?: () => void) => {
    if (!toBranch) {
      showMessage('Please select a destination branch.', 'error');
      return;
    }

    if (!amount.length) {
      showMessage('Please enter transfer amount.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_ENDPOINT}/api/v1/branch-invoices/`,
        {
          to_branch: { id: toBranch.id },
          amount_input: amount.map(([currency, value]) => [currency, value.toString()]),
        },
        config
      );
      showMessage('Branch transfer invoice created.', 'success');
      setToBranch(null);
      setAmount([]);
      // Refresh invoices after creating a new one
      refetchInvoices();
      if (onSuccess) onSuccess();
    } catch (e: unknown) {
      let detail = 'Failed to create branch transfer.';
      if (axios.isAxiosError(e)) {
        detail = e.response?.data?.error?.[0] ?? e.response?.data?.detail ?? detail;
      }
      showMessage(detail, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelInvoice = async (invoice: IBranchInvoice) => {
    try {
      await axios.patch(
        `${API_ENDPOINT}/api/v1/branch-invoice/${invoice.id}/`,
        { status: BranchInvoiceState.CANCELLED },
        config
      );
      refetchInvoices();
      showMessage('Invoice cancelled successfully.', 'success');
      setCancellingInvoice(null);
    } catch (e: unknown) {
      let detail = 'Failed to cancel invoice.';
      if (axios.isAxiosError(e)) {
        detail = e.response?.data?.error?.[0] ?? e.response?.data?.detail ?? detail;
      }
      showMessage(detail, 'error');
    }
  };

  const handleUpdateInvoice = () => {
    refetchInvoices();
  };

  const downloadInvoicePDF = async (invoice: IBranchInvoice) => {
    setDownloadingInvoice(invoice);
    const endpoint = `${API_ENDPOINT}/api/v1/branch-invoice/${invoice.id}/print/`;

    try {
      const response= await axios.get(endpoint, {
        responseType: 'arraybuffer',
        ...config
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const formattedDate = format(new Date(invoice.created_at), 'dd-MM-yyyy');

      const link = document.createElement('a');
      link.href = url;
      link.download = `branch_invoice_${invoice.id}.${invoice.from_branch.name}_to_${invoice.to_branch.name}.${formattedDate}.pdf`;
      link.click();

      URL.revokeObjectURL(url);
      showMessage('Invoice downloaded successfully', 'success');
    } catch (e: unknown) {
      let detail = 'Failed to download invoice.';
      if (axios.isAxiosError(e)) {
        detail = e.response?.data?.error?.[0] ?? e.response?.data?.detail ?? detail;
      }
      showMessage(detail, 'error');
    }
    setDownloadingInvoice(null);
  };

  return (
    <Box sx={{ mt: 2, maxWidth: 1200, mx: 'auto' }}>
      {/* Header + filters + New Transfer button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <InvoiceDirectionFilterChips
            direction={directionFilter}
            onChange={setDirectionFilter}
          />
        </Box>
        <NewTransferButton
          branches={branches}
          selectedBranchId={selectedBranch?.id ?? null}
          loadingBranches={loadingBranches}
          isSubmitting={isSubmitting}
          toBranch={toBranch}
          setToBranch={setToBranch}
          amount={amount}
          setAmount={setAmount}
          onCreate={handleCreateTransfer}
        />
      </Box>

      {/* Invoices list */}
      <Box sx={{ mt: 3 }}>
        <GenericTable<IBranchInvoice>
          sourceView="branch-invoices"
          items={invoices}
          getItemUID={(inv) => String(inv.id)}
          selectedItems={new Map()}
          isLoading={loadingInvoices}
          fullWidth
          columns={[
            {
              id: 'id',
              header: <b>ID</b>,
              display_body: (inv) => <b>{inv.id}</b>,
            },
            {
              id: 'from',
              header: <b>From</b>,
              display_body: (inv) => <>{inv.from_branch?.name ?? '-'}</>,
            },
            {
              id: 'to',
              header: <b>To</b>,
              display_body: (inv) => <>{inv.to_branch?.name ?? '-'}</>,
            },
            {
              id: 'amount',
              header: <b>Amount</b>,
              display_body: (inv) => <>{displayMoney(inv.amount ?? [])}</>,
            },
            {
              id: 'status',
              header: <b>Status</b>,
              display_body: (inv) => <>{showBranchInvoiceState(inv.status)}</>,
            },
            {
              id: 'created_at',
              header: <b>Created At</b>,
              display_body: (inv) => (
                <>
                  {inv.created_at
                    ? new Date(inv.created_at).toLocaleString()
                    : '-'}
                </>
              ),
            },
            {
              id: 'view',
              header: <b>View</b>,
              display_body: (inv) => (
                <IconButton
                  color="primary"
                  onClick={() => setSelectedInvoice(inv)}
                  disabled={inv.status === BranchInvoiceState.CANCELLED}
                >
                  <VisibilityIcon />
                </IconButton>
              ),
            },
            {
              id: 'download',
              header: <b>Download</b>,
              display_body: (inv) => (
                <IconButton
                  color="primary"
                  onClick={() => downloadInvoicePDF(inv)}
                  disabled={inv.status === BranchInvoiceState.CANCELLED || downloadingInvoice?.id === inv.id}
                >
                  {downloadingInvoice?.id === inv.id ? (
                    <CircularProgress size={24} />
                  ) : (
                    <DownloadIcon />
                  )}
                </IconButton>
              ),
            },
            {
              id: 'cancel',
              header: <b>Cancel</b>,
              display_body: (inv) => (
                <IconButton
                  color="error"
                  disabled={!canCancelBranchInvoice(inv)}
                  onClick={() => setCancellingInvoice(inv)}
                >
                  <CancelIcon />
                </IconButton>
              ),
            },
          ]}
        />
      </Box>

      {/* Invoice Detail Dialog */}
      {selectedInvoice && (
        <CustomDialog
          open={true}
          onClose={() => setSelectedInvoice(null)}
          maxWidth="lg"
          fullWidth
        >
          <BranchInvoiceDetail
            invoice={selectedInvoice}
            updateInvoice={handleUpdateInvoice}
            onClose={() => setSelectedInvoice(null)}
          />
        </CustomDialog>
      )}

      {/* Cancel Invoice Confirmation */}
      {cancellingInvoice && (
        <ConfirmAction
          message={`Are you sure you want to cancel invoice #${cancellingInvoice.id}?`}
          action={() => handleCancelInvoice(cancellingInvoice)}
          isOpen={true}
          handleClose={() => setCancellingInvoice(null)}
          isSevereAction={true}
          confirmSevereActionTarget={`cancel invoice`}
        >
          <Typography variant="body2" color="text.secondary">
            This will cancel the invoice and all its pending payment tasks. This action cannot be undone.
          </Typography>
        </ConfirmAction>
      )}
    </Box>
  );
}

export default BranchTransfers;

// ---------------- New Transfer Dialog ----------------

interface NewTransferButtonProps {
  branches: BranchType[];
  selectedBranchId: string | number | null;
  loadingBranches: boolean;
  isSubmitting: boolean;
  toBranch: BranchType | null;
  setToBranch: (b: BranchType | null) => void;
  amount: Money[];
  setAmount: (m: Money[]) => void;
  onCreate: (onSuccess?: () => void) => Promise<void>;
}

const NewTransferButton: React.FC<NewTransferButtonProps> = ({
  branches,
  selectedBranchId,
  loadingBranches,
  isSubmitting,
  toBranch,
  setToBranch,
  amount,
  setAmount,
  onCreate,
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setToBranch(null);
    setAmount([]);
  };

  const handleSubmit = async () => {
    await onCreate(handleClose);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        New Transfer
      </Button>

      <CustomDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            New Branch Transfer
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the destination branch and enter the transfer amount.
          </Typography>

          {loadingBranches ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="to-branch-label">Destination Branch</InputLabel>
                <Select
                  labelId="to-branch-label"
                  label="Destination Branch"
                  value={toBranch?.id ?? ''}
                  onChange={(e) => {
                    const b = branches.find((x) => x.id === e.target.value);
                    setToBranch(b ?? null);
                  }}
                >
                  {branches
                    .filter((b) => b.id !== selectedBranchId)
                    .map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Box sx={{ mb: 2 }}>
                <MoneyInput
                  money={amount}
                  setMoney={setAmount}
                  label="Transfer Amount"
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                <Button onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Transfer'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </CustomDialog>
    </>
  );
};

// ---------------- Filter for Incoming / Outgoing ----------------

type Direction = 'incoming' | 'outgoing';

interface DirectionOption {
  id: Direction;
  label: string;
}

const DIRECTION_OPTIONS: DirectionOption[] = [
  { id: 'incoming', label: 'Incoming' },
  { id: 'outgoing', label: 'Outgoing' },
];

interface InvoiceDirectionFilterChipsProps {
  direction: Direction;
  onChange: (dir: Direction) => void;
}

const InvoiceDirectionFilterChips: React.FC<InvoiceDirectionFilterChipsProps> = ({
  direction,
  onChange,
}) => {
  const selectedOption =
    DIRECTION_OPTIONS.find((opt) => opt.id === direction) || DIRECTION_OPTIONS[1];

  return (
    <FilterChips<DirectionOption>
      label=""
      options={DIRECTION_OPTIONS}
      multiple={false}
      selected={[selectedOption]}
      onChange={(selected) => {
        const next =
          selected[0]?.id ??
          (direction === 'incoming' ? 'outgoing' : 'incoming');
        onChange(next);
      }}
      getOptionLabel={(opt) => opt.label}
      optionsEqual={(a, b) => a.id === b.id}
    />
  );
};

